import Link from 'next/link'

import { Poster } from '@/app/components/atoms/Poster'
import { Price } from '@/app/components/atoms/Price'
import { PriceTag } from '@/app/components/atoms/PriceTag'
import { Sticker } from '@/app/components/atoms/Sticker'
import { Tag } from '@/app/components/atoms/Tag'
import { cx } from '@/app/lib/utils'

import styles from './OtherLeftProductCard.module.scss'
import type { OtherLeftProductCardData } from './types'

// `id` es solo la clave de lista del padre (`OtherLeftProductRail.tsx`), no
// un dato que la tarjeta necesite renderizar — se excluye de las props.
interface OtherLeftProductCardProps extends Omit<OtherLeftProductCardData, 'id'> {
  readonly className?: string
}

/**
 * Tarjeta compartida del módulo "otros productos izquierda" — imagen 1:1 +
 * fecha/título/ciudad/precio, SIN botón de compra propio: toda la tarjeta es
 * un único `<Link>` a la ficha del producto (patrón de `RecentlyViewedCard`,
 * no el *stretched-link* de `VerticalCard` — aquí no hace falta dejar sitio a
 * un CTA independiente porque no existe).
 *
 * Promovida desde `modules/OtrosProductosIzq/OtrosProductosIzqCard/`
 * (renombrada, sin cambios de lógica/JSX — specs/051-canales-modulo-otros-
 * productos-izq/research.md R-01/R-06): la consumen tanto el módulo CMS
 * general (`modules/OtrosProductosIzq/`) como el módulo de canal
 * (`channelModules/OtherLeftProduct/`), que solo difieren en cómo resuelven
 * `href` antes de llegar aquí.
 *
 * Presentacional pura (sin estado, efectos ni lógica propia) → Server
 * Component, sin test propio (Constitución, Principio IX — mismo criterio
 * que `RecentlyViewedCard`). Renderizado defensivo: imagen, ciudad y precio
 * se omiten si no hay dato.
 *
 * Insignia de estado (`imageBadge`) y etiqueta de categoría (`tag`) son
 * mutuamente excluyentes sobre la imagen — cuando el producto no está
 * disponible para compra, la insignia sustituye a la categoría (mismo
 * criterio que `VerticalCard`/`RecentlyViewedCard`, con la precedencia que ya
 * resuelve `resolvePurchaseState`).
 *
 * `accentColor` en `Price`: el importe rebajado se pinta en negro (paridad
 * con `ProductCardVertical` de la referencia, `.priceFinal`), a diferencia
 * de `VerticalCard`/`RecentlyViewedCard` (rojo de promoción, paridad con
 * `EventCard`). El badge de descuento (`PriceTag`) sí es siempre rojo
 * (`--color-promo`), sin variante — misma insignia en toda la web.
 */
export function OtherLeftProductCard({
  title,
  href,
  img,
  date,
  venue,
  priceDisplay,
  tag,
  sticker,
  imageBadge,
  className,
}: OtherLeftProductCardProps) {
  return (
    <article className={cx(styles.card, className)}>
      <Link href={href} className={styles.link}>
        <div className={cx(styles.imageWrap, imageBadge && styles.imageWrapMuted)}>
          {img && <Poster src={img} alt="" className={styles.poster} />}

          {tag && !imageBadge && (
            <Tag variant="black" className={styles.tagTopLeft}>
              {tag}
            </Tag>
          )}

          <PriceTag
            tag={priceDisplay?.tag ?? null}
            hasAppPromotions={priceDisplay?.hasAppPromotions}
            className={styles.tagTopRight}
          />

          {imageBadge && (
            <div className={styles.imageBadge}>
              <Tag variant="status">{imageBadge}</Tag>
            </div>
          )}

          {/* Sticker comercial — esquina inferior izquierda de la imagen. Se
              oculta con estado de compra (igual que la categoría): un evento
              no comprable no promociona "Últimas entradas". */}
          {sticker && !imageBadge && (
            <div className={styles.stickerBottomLeft}>
              <Sticker>{sticker}</Sticker>
            </div>
          )}
        </div>

        <div className={styles.body}>
          {date && <p className={styles.date}>{date}</p>}
          {/* h2, no h3: el título de la franja (`OtrosProductosIzq.tsx`/
              `OtherLeftProduct.tsx` de canal), cuando existe, también es un
              `<h2>` — la página anfitriona no intercala ningún h2 entre su
              propio h1 y este módulo. Usar h3 aquí saltaría un nivel cuando
              el módulo no tiene título propio (mismo criterio que
              `VerticalCard`, specs/023-productos-destacados-module). */}
          <h2 className={styles.title}>{title}</h2>
          {venue && <p className={styles.venue}>{venue}</p>}

          {priceDisplay && (
            <Price
              amount={priceDisplay.amount}
              isFree={priceDisplay.isFree}
              originalAmount={priceDisplay.originalAmount ?? undefined}
              variant={priceDisplay.originalAmount != null ? 'offer' : 'default'}
              showFrom
              accentColor
              className={styles.price}
            />
          )}
        </div>
      </Link>
    </article>
  )
}
